import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import axios from 'axios'
import useShop from '../context/AppContext'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

const StyledForm = styled.div`
  width: 100%;
`
const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 18px 32px 32px 32px;
  border-radius: 8px;
  background-color: var(--black-medium);
  @media screen and (max-width: 1023px) {
    padding: 18px 25px 32px 25px;
  }
`
const StyledColumns = styled.div`
  display: flex;
  flex-direction: row;
  label {
    display: block;
    margin: 24px 0 12px 12px;
    color: var(--white);
    font-family: var(--font-sans);
    font-size: var(--fz-sm);
    font-weight: 600;
    line-height: 1;
  }
  input[type='email'],
  input[type='text'],
  textarea {
    width: 100%;
    height: 48px;
    padding: 7px 12px 10px;
    vertical-align: middle;
    color: var(--text);
    font-family: var(--font-condensed);
    font-size: var(--fz-md);
    text-align: left;
    border-radius: 4px;
    border: 1px solid var(--border-dark);
    background-color: var(--black-light);
    &:focus {
      outline: 0;
      color: var(--text);
      border-color: var(--text);
      background-color: var(--black-light);
    }
    &.error {
      border-color: var(--error);
    }
  }
  textarea {
    height: auto;
    min-height: 148px;
    padding-top: 12px;
  }
  .input-error {
    display: block;
    color: var(--error);
    padding: 5px 10px 0 13px;
    font-size: var(--fz-xs);
    line-height: 1.15;
  }
  @media screen and (max-width: 800px) {
    flex-direction: column;
  }
`
const LeftColumn = styled.div`
  width: 50%;
  padding-right: 20px;
  @media screen and (max-width: 800px) {
    width: 100%;
    padding-right: 0;
  }
`
const RightColumn = styled.div`
  width: 50%;
  padding-left: 20px;
  .file-wrapper {
    padding: 10px 14px 10px;
    color: var(--text);
    font-family: var(--font-condensed);
    font-size: var(--fz-md);
    text-align: left;
    border-radius: 4px;
    border: 1px solid var(--border-dark);
    background-color: var(--black-light);
  }
  @media screen and (max-width: 800px) {
    width: 100%;
    padding-left: 0;
  }
`
const SuccessBox = styled.div`
  width: calc(50% - 20px);
  margin: 36px 0 12px calc(50% + 20px);
  padding: 20px 20px 25px;
  color: var(--white);
  font-family: var(--font-condensed);
  font-size: var(--fz-lg);
  font-weight: 500;
  text-align: center;
  line-height: 1;
  border-radius: 4px;
  background-color: var(--secondary);
  @media screen and (max-width: 1220px) {
    width: 100%;
    margin: 36px 0 12px 0;
  }
`
const FailureBox = styled.div`
  width: calc(50% - 20px);
  margin: 36px 0 12px calc(50% + 20px);
  padding: 20px 20px 25px;
  color: var(--white);
  font-family: var(--font-condensed);
  font-size: var(--fz-lg);
  font-weight: 500;
  text-align: center;
  line-height: 1;
  border-radius: 4px;
  background-color: var(--grey-medium);
  @media screen and (max-width: 1220px) {
    width: 100%;
    margin: 36px 0 12px 0;
  }
`
const WaitingBox = styled.div`
  width: calc(50% - 20px);
  margin: 36px 0 12px calc(50% + 20px);
  padding: 20px 20px 25px;
  text-align: center;
  span {
    display: inline-block;
    width: 8px;
    height: 8px;
    margin: 0 3px;
    border-radius: 50%;
    background-color: var(--grey-medium);
    animation-name: blink;
    animation-duration: 1s;
    animation-iteration-count: infinite;
    animation-fill-mode: both;
  }
  span:nth-child(2) {
    animation-delay: 0.2s;
  }
  span:nth-child(3) {
    animation-delay: 0.4s;
  }
  @keyframes blink {
    0% {
      opacity: 0.3;
    }
    20% {
      opacity: 1;
    }
    100% {
      opacity: 0.3;
    }
  }
  @media screen and (max-width: 1220px) {
    width: 100%;
    margin: 36px 0 12px 0;
  }
`
const SubmitBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  margin: 30px 0 0 0;
  p.small {
    width: 100%;
    font-size: var(--fz-xxs);
    color: var(--text);
    padding: 0;
    margin: 0 30px 0 30px;
  }
  .button {
    padding-left: 40px;
    padding-right: 40px;
    width: auto;
  }
  @media screen and (max-width: 800px) {
    flex-direction: column;
    p.small {
      width: 100%;
      margin: 0 0 20px 0;
    }
    .button {
      width: 100%;
      margin: 0;
    }
  }
`

const ServiceForm = () => {
  const ERROR_NAME_1 = 'Please include your name'
  const ERROR_NAME_2 = 'Is this really your name?'
  const ERROR_EMAIL_1 = 'Please include your email address so that I can reply'
  const ERROR_EMAIL_2 = 'Sorry, this does not look like a valid email address, my email is on the top/left if you would like to email me directly instead'
  const ERROR_MESSAGE_1 = "There's no project brief here; what is it that I can help you with?"
  const ERROR_MESSAGE_2 = "Your project brief isn't very long is it?"
  const SUCCESS_MESSAGE = 'Thank You! Your project brief has been successfully sent. I will contact you shortly!'
  const FAILURE_MESSAGE = 'Sorry, your project brief was not sent successfully. Please try again or contact us directly.'

  const { serviceSubject } = useShop()

  const [wait, setWait] = useState(false)
  const [success, setSuccess] = useState(false)
  const [failure, setFailure] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [project, setProject] = useState('')
  const [product, setProduct] = useState('')
  const [find, setFind] = useState('')
  const [message, setMessage] = useState('')

  const [nameError, setNameError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [messageError, setMessageError] = useState('')

  const myRef = useRef(null)
  const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  const privacylink = (
    <Link to="/privacy-policy" className="secondary-link">
      privacy policy
    </Link>
  )

  const clearForm = () => {
    setWait(false)
    setName('')
    setEmail('')
    setWebsite('') / setProduct('')
    setFind('')
    setMessage('')
  }

  const executeIntoView = () => myRef.current.scrollIntoView()

  const handleChangeName = e => {
    const value = typeof e.target.value === 'string' ? e.target.value : ''
    setName(value)
  }

  const handleChangeEmail = e => {
    const value = typeof e.target.value === 'string' ? e.target.value : ''
    setEmail(value.toLowerCase())
  }

  const handleChangeWebsite = e => {
    const value = typeof e.target.value === 'string' ? e.target.value : ''
    setWebsite(value.toLowerCase())
  }

  const handleChangeProject = e => {
    const value = typeof e.target.value === 'string' ? e.target.value : ''
    setProject(value)
  }

  const handleChangeProduct = e => {
    const value = typeof e.target.value === 'string' ? e.target.value : ''
    setProduct(value)
  }

  const handleChangeFind = e => {
    const value = typeof e.target.value === 'string' ? e.target.value : ''
    setFind(value)
  }

  const handleChangeMessage = e => {
    const value = typeof e.target.value === 'string' ? e.target.value : ''
    setMessage(value)
  }

  const checkNameStatus = () => {
    if (name.length === 0) {
      setNameError(ERROR_NAME_1)
      return false
    } else if (name.length > 0 && name.length < 3) {
      setNameError(ERROR_NAME_2)
      return false
    } else {
      setNameError('')
    }
    return true
  }

  const checkEmailStatus = () => {
    if (email.length === 0) {
      setEmailError(ERROR_EMAIL_1)
      return false
    } else if (email.length > 0 && !emailPattern.test(email)) {
      setEmailError(ERROR_EMAIL_2)
      return false
    } else {
      setEmailError('')
    }
    return true
  }

  const checkMessageStatus = () => {
    if (message.length === 0) {
      setMessageError(ERROR_MESSAGE_1)
      return false
    } else if (message.length > 0 && message.length < 18) {
      setMessageError(ERROR_MESSAGE_2)
      return false
    } else {
      setMessageError('')
    }
    return true
  }

  const handleSubmit = e => {
    e.preventDefault()
    const correctName = checkNameStatus()
    const correctEmail = checkEmailStatus()
    const correctMessage = checkMessageStatus()
    if (correctName && correctEmail && correctMessage) {
      executeIntoView()
      executeContacEmail()
    } else {
      executeIntoView()
    }
  }

  const executeContacEmail = () => {
    setWait(true)
    const form = new FormData()
    const service = project ? project : serviceSubject
    form.append('action', 'execute_shopify_service_email')
    form.append('security', roberteberhard_data.ajax_nonce)
    form.append('name', name)
    form.append('email', email)
    form.append('website', website)
    form.append('project', service)
    form.append('product', product)
    form.append('find', find)
    form.append('message', message)
    form.append('file', document.querySelector('#form_file').files[0])

    axios
      .post(roberteberhard_data.ajax_url, form)
      .then(function (response) {
        if (response.data.data === 'success') {
          setSuccess(true)
        }
        if (response.data.data === 'failure') {
          setSuccess(true)
          setFailure(true)
        }
      })
      .catch(function () {
        setSuccess(true)
        setFailure(true)
      })
  }

  /*   axios({
    method: 'post',
    url: 'your_url',
    data: formData,
    headers: {
        "X-CSRFToken": CSRF_TOKEN, # django security
        "content-type": "multipart/form-data"
    }
}).then(function (response) {
    # success
});
 */

  useEffect(() => {
    setSuccess(false)
    setFailure(false)
    clearForm()
  }, [])

  return (
    <StyledForm ref={myRef}>
      <form enctype="multipart/form-data" onSubmit={handleSubmit}>
        <StyledContent>
          <StyledColumns>
            <LeftColumn>
              <label for="Name">Name</label>
              <input type="text" value={name} onChange={handleChangeName} onBlur={checkNameStatus} className={nameError.length > 0 ? 'error' : ''} maxlength="256" name="Name" placeholder="How should I call you?" />
              {nameError.length > 0 ? <span className="input-error">{nameError}</span> : ''}
              <label for="Email">Email</label>
              <input type="text" value={email} onChange={handleChangeEmail} onBlur={checkEmailStatus} className={emailError.length > 0 ? 'error' : ''} maxlength="256" name="Email" placeholder="Your email address" />
              {emailError.length > 0 ? <span className="input-error">{emailError}</span> : ''}
              {!success ? <label for="Website">Current website</label> : ''}
              {!success ? <input type="text" value={website} onChange={handleChangeWebsite} maxlength="256" name="Website" placeholder="Your website - e.g. example.com" /> : ''}
              {!success ? <label for="Project">Service type</label> : ''}
              {project ? !success ? <input type="text" value={project} onChange={handleChangeProject} maxlength="256" name="Project" placeholder="Theme Tweaks & Changes" /> : '' : !success ? <input type="text" value={serviceSubject} onChange={handleChangeProject} maxlength="256" name="Project" placeholder="Theme Tweaks & Changes" /> : ''}
            </LeftColumn>
            <RightColumn>
              <label for="Product">What do you sell</label>
              <input type="text" value={product} onChange={handleChangeProduct} maxlength="256" name="Product" placeholder="I sell jewelry, cosmetics, sportswear.." />
              <label for="Find">How did you find me?</label>
              <input type="text" value={find} onChange={handleChangeFind} maxlength="256" name="Find" placeholder="Google, Instagram, Facebook, LinkedIn..." />
              {!success ? <label for="Message">Write a project brief below</label> : ''}
              {!success ? <textarea value={message} onChange={handleChangeMessage} onBlur={checkMessageStatus} className={messageError.length > 0 ? 'error' : ''} maxlength="5000" name="Message" placeholder="In order for me to provide you with an accurate quote, please provide me with the following information: who you are, project description, and project goal"></textarea> : ''}
              {messageError.length > 0 ? <span className="input-error">{messageError}</span> : ''}
              {!success ? <label for="Product">Attach File</label> : ''}
              {!success ? (
                <div className="file-wrapper">
                  <input type="file" id="form_file" accept=".jpg, .pdf, .odt, .doc, .docx" name="form_file" required="" />
                </div>
              ) : (
                ''
              )}
            </RightColumn>
          </StyledColumns>

          {success ? (
            <motion.div animate={{ opacity: [0, 1] }} transition={{ duration: 0.5 }} className="start-opacity">
              {!failure ? <SuccessBox>{SUCCESS_MESSAGE}</SuccessBox> : <FailureBox>{FAILURE_MESSAGE}</FailureBox>}
            </motion.div>
          ) : !wait ? (
            ''
          ) : (
            <WaitingBox>
              <span></span>
              <span></span>
              <span></span>
            </WaitingBox>
          )}
        </StyledContent>
        {!success ? (
          !wait ? (
            <SubmitBox>
              <p className="small">By clicking submit, you agree to send your information to Robert Eberhard who agrees to use it according to its {privacylink}.</p>
              <input type="submit" className="button" value="Submit" />
            </SubmitBox>
          ) : (
            ''
          )
        ) : (
          ''
        )}
      </form>
    </StyledForm>
  )
}

export default ServiceForm
